import { sanitizeAiChatHtml } from '../utils/aiChatSanitize'

export const AI_CHAT_STORAGE_PREFIX = 'ai-chat-history-v1'
export const AI_CHAT_STORAGE_EVENT = 'ai-chat-storage'

function pendingKey(storageKey) {
  return `${storageKey}:pending`
}

function errorKey(storageKey) {
  return `${storageKey}:lastError`
}

export function buildAiChatStorageKey(userId) {
  return `${AI_CHAT_STORAGE_PREFIX}:${userId || 'guest'}`
}

export function readAiChatMessages(storageKey) {
  if (typeof window === 'undefined') {
    return []
  }
  try {
    const raw = window.sessionStorage.getItem(storageKey)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeAiChatMessages(storageKey, messages) {
  if (typeof window === 'undefined') {
    return
  }
  window.sessionStorage.setItem(storageKey, JSON.stringify(messages))
}

export function isAiChatPending(storageKey) {
  if (typeof window === 'undefined') {
    return false
  }
  return window.sessionStorage.getItem(pendingKey(storageKey)) != null
}

export function readAiChatLastError(storageKey) {
  if (typeof window === 'undefined') {
    return ''
  }
  return String(window.sessionStorage.getItem(errorKey(storageKey)) || '')
}

function emitAiChatStorageUpdate(storageKey) {
  if (typeof window === 'undefined') {
    return
  }
  window.dispatchEvent(
    new CustomEvent(AI_CHAT_STORAGE_EVENT, { detail: { storageKey } }),
  )
}

/**
 * Удаляет историю, pending и ошибку; уведомляет подписчиков.
 */
export function clearAiChatStorage(storageKey) {
  if (typeof window === 'undefined') {
    return
  }
  window.sessionStorage.removeItem(storageKey)
  window.sessionStorage.removeItem(pendingKey(storageKey))
  window.sessionStorage.removeItem(errorKey(storageKey))
  emitAiChatStorageUpdate(storageKey)
}

/**
 * Записывает user-сообщение, ставит pending, дергает API; по ответу обновляет storage.
 * Повторный вызов при активном pending игнорируется.
 */
export function startAiChatSend({ storageKey, trimmedText, client_context, api }) {
  if (typeof window === 'undefined' || !trimmedText || isAiChatPending(storageKey)) {
    return
  }

  const messages = readAiChatMessages(storageKey)
  const userMessage = {
    id: `user-${Date.now()}`,
    role: 'user',
    text: trimmedText,
  }
  writeAiChatMessages(storageKey, [...messages, userMessage])
  window.sessionStorage.setItem(pendingKey(storageKey), JSON.stringify({ startedAt: Date.now() }))
  window.sessionStorage.removeItem(errorKey(storageKey))
  emitAiChatStorageUpdate(storageKey)

  api
    .post('/ai/chat', {
      message: trimmedText,
      client_context: client_context || '',
    })
    .then((response) => {
      const replyHtml = sanitizeAiChatHtml(response.data?.reply_html || '')
      const replyText = String(response.data?.reply_text || '').trim()
      const current = readAiChatMessages(storageKey)
      const assistantMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        text: replyText,
        html: replyHtml,
      }
      writeAiChatMessages(storageKey, [...current, assistantMessage])
      window.sessionStorage.removeItem(pendingKey(storageKey))
      emitAiChatStorageUpdate(storageKey)
    })
    .catch((requestError) => {
      const errorMessage =
        requestError?.response?.data?.error || 'Не удалось получить ответ ассистента'
      window.sessionStorage.setItem(errorKey(storageKey), errorMessage)
      window.sessionStorage.removeItem(pendingKey(storageKey))
      emitAiChatStorageUpdate(storageKey)
    })
}
